import React from 'react'
import Slider from 'react-slick'
import {
  makeStyles,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@material-ui/core'
import moment from 'moment'
import StyledSVG from '../shared/StyledSVG'
import emptyDataPreviewSVG from '../../assets/icons/illustration-medical-clipboard-form.svg'
import UserAvatar from '../shared/UserAvatar'

const useStyles = makeStyles(theme => ({
  infirmary_slider_root: {
    paddingBottom: 5,
    overflow: 'hidden',
    '& .slick-slide': {
      padding: '0px 10px',
      width: 305,
      height: 410,
      [theme.breakpoints.down('1600')]: {
        height: 435
      },
      [theme.breakpoints.down('600')]: {
        width: 300,
        padding: '0px 5px'
      }
    },
    '& .slick-list': {
      width: '100%',
      margin: '0px -10px',
      [theme.breakpoints.down('600')]: {
        margin: '0px 10px'
      }
    },
    '& .slick-dots': {
      bottom: '-12px',
      [theme.breakpoints.down('1600')]: {
        bottom: '-11px'
      }
    }
  },
  answer_date: {
    marginTop: 0
  },
  title: {
    color: '#386093',
    fontSize: 28,
    fontWeight: 600,
    marginTop: 0,
    marginBottom: 5,
    [theme.breakpoints.down('920')]: {
      margin: '20px 0px'
    },
    [theme.breakpoints.down('600')]: {
      textAlign: 'center'
    }
  },
  answer_card: {
    cursor: 'pointer',
    borderRadius: 16,
    padding: '24px',
    height: 402,
    minHeight: 200,
    [theme.breakpoints.down('1600')]: {
      height: 427
    }
  },
  last_answer_color: {
    color: '#FFFFFF',
    backgroundColor: '#EF7C8F'
  },
  answer_color: {
    color: '#555555',
    backgroundColor: '#FFFFFF'
  },
  user_infos: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    '& .name': {
      fontSize: 18,
      margin: 0
    },
    '& .classroom': {
      fontSize: 14,
      margin: 0
    }
  },
  parent_info: {
    marginTop: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    '& .parent_label, .parent_name, .parent_phone': {
      margin: 0
    },
    '& .parent_label': {
      fontSize: 12
    },
    '& .parent_name, .parent_phone, .parent_email': {
      fontSize: 14
    },
    '& .parent_email': {
      wordBreak: 'break-all'
    }
  },
  avatar_section: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4
  },
  symptom_description: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  empty_answers: {
    height: 430,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    rowGap: 16,
    '& .description': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      '& h1': {
        margin: 0,
        color: '#386093',
        fontSize: 24
      },
      '& p': {
        margin: 0,
        fontWeight: 400,
        fontSize: 16,
        textAlign: 'center'
      }
    },
    [theme.breakpoints.down('1600')]: {
      height: 465
    }
  }
}))

const InfirmarySlider = ({
  getReportStudentsAnswers,
  userId,
  reportAnswers,
  history,
  currentSchool
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('1600'))
  const [isDragging, setIsDragging] = React.useState(false)
  React.useEffect(() => {
    getReportStudentsAnswers({
      params: {
        'q[tag_tag_order_eq]': 25,
        'q[report_student_report_report_template_classroom_school_id_eq]':
          currentSchool?.id,
        'page[size]': 10,
        'q[s]': 'created_at desc'
      }
    })
  }, [])
  const emptyAnswers =
    !reportAnswers.isFetching && reportAnswers.items.length === 0

  const lastAnswer = React.useMemo(
    () => {
      if (reportAnswers.items?.length > 0) {
        const now = new Date()
        return reportAnswers?.items.reduce((prev, curr) => {
          const prevDiff = Math.abs(new Date(prev.created_at) - now)
          const currDiff = Math.abs(new Date(curr.created_at) - now)
          return currDiff < prevDiff ? curr : prev
        })
      }
    },
    [reportAnswers]
  )

  const handleClickItem = (e, answer) => {
    if (isDragging) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    const reportId = answer?.report_student?.report_id
    const reportStudentId = answer?.report_student_id
    history.push(`/reports/${reportId}/classroom/${reportStudentId}`)
  }

  if (reportAnswers?.isFetching) {
    return (
      <div className={classes.empty_answers}>
        <CircularProgress />
      </div>
    )
  }
  return (
    <div className={classes.infirmary_slider_root}>
      <h1 className={classes.title}>Enfermaria</h1>
      {emptyAnswers ? (
        <div className={classes.empty_answers}>
          <StyledSVG src={emptyDataPreviewSVG} width={170} height={150} />
          <div className='description'>
            <h1>Nenhuma ocorrência registrada</h1>
            <p>
              Quando houver atendimentos na enfermaria, eles aparecerão aqui.
            </p>
          </div>
        </div>
      ) : (
        <Slider
          dots
          infinite={false}
          adaptiveHeight
          variableWidth
          slidesToShow={matches ? 1 : 2}
          slidesToScroll={matches ? 1 : 2}
          beforeChange={() => setIsDragging(true)}
          afterChange={() => setIsDragging(false)}
        >
          {reportAnswers?.items.length > 0 &&
            reportAnswers?.items.map(answer => {
              const symptoms = answer.multiple_answers.join('; ')
              const answerDate = moment(answer.created_at).format('DD/MM/YYYY')
              const answerHour = moment(answer.created_at).format('HH:mm')
              return (
                <div
                  key={answer.id}
                  onClick={event => handleClickItem(event, answer)}
                  className={`${classes.answer_card} ${
                    lastAnswer?.id === answer.id
                      ? classes.last_answer_color
                      : classes.answer_color
                  }`}
                >
                  <p className={classes.answer_date}>
                    {answerDate} - {answerHour}
                  </p>
                  <div className={classes.avatar_section}>
                    <UserAvatar
                      avatarStyle={{ width: 56, height: 56, borderRadius: 16 }}
                      avatarUrl={answer?.report_student?.child?.avatar_url}
                      userName={answer?.report_student?.child?.name}
                      withoutUserName
                    />
                    <div className={classes.user_infos}>
                      <h1 className='name'>
                        {answer.report_student.child?.name}
                      </h1>
                      <p className='classroom'>
                        {
                          answer.report_student.report.report_template
                            .classroom?.[0].name
                        }
                      </p>
                    </div>
                  </div>
                  <div className={classes.parent_info}>
                    <p className='parent_label'>Responsável</p>
                    <p className='parent_name'>
                      {answer.report_student.child?.parents?.[0]?.name}
                    </p>
                    {answer.report_student.child?.parents?.[0]?.phone && (
                      <p className='parent_phone'>
                        {answer.report_student.child.parents[0].phone}
                      </p>
                    )}
                    {answer.report_student.child?.parents?.[0]?.email && (
                      <p className='parent_email'>
                        {answer.report_student.child.parents[0].email}
                      </p>
                    )}
                  </div>
                  <p>
                    <i>{symptoms}</i>
                  </p>
                  <p className={classes.symptom_description}>{answer.answer}</p>
                </div>
              )
            })}
        </Slider>
      )}
    </div>
  )
}

export default InfirmarySlider
